import { useSyncExternalStore } from 'react'
import { useDemo, type DataState } from '@/lib/demo/demo-context'

function subscribe(onChange: () => void) {
  window.addEventListener('online', onChange)
  window.addEventListener('offline', onChange)
  return () => {
    window.removeEventListener('online', onChange)
    window.removeEventListener('offline', onChange)
  }
}

/** Whether the browser says it has a network. */
export function useOnline(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => navigator.onLine,
    () => true,
  )
}

export interface DataStateInput {
  /** The screen's data is still loading for the first time. */
  isPending?: boolean
  /** The data could not be loaded. */
  isError?: boolean
  /** Loaded, and there is nothing yet: the first-run empty state. */
  isEmpty?: boolean
  /** Loaded, but part of it is still processing or delayed. */
  isPartial?: boolean
}

/**
 * Which of the six designed states a screen is in. A screen with no API yet passes
 * `{ isEmpty: true }` and shows the design's first-run empty state. With the dev
 * prototype controls on, their "Data state" wins.
 */
export function useDataState({ isPending, isError, isEmpty, isPartial }: DataStateInput): DataState {
  const demo = useDemo()
  const online = useOnline()
  if (import.meta.env.DEV && demo.enabled) return demo.dataState
  if (!online) return 'offline'
  if (isPending) return 'loading'
  if (isError) return 'error'
  if (isEmpty) return 'empty'
  if (isPartial) return 'partial'
  return 'populated'
}
